import React, { useEffect, useRef, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { useTranslation } from "../../context/TranslationContext";
import { useCurrency } from "../../context/CurrencyContext";
import './home.css'; // Reusing home styles for consistency
import { useNavigate } from "react-router-dom";
import ApiProvider from "../../apiProvider/categoryApi";
import { IMAGE_URL } from "../../network/apiClient";
import FilterSidebar from "../../components/FilterSidebar";

const SAREE_SCROLL_KEY = 'saree_scroll_pos';
const SAREE_LIMIT_KEY = 'saree_limit';
const SAREE_PRODUCTS_KEY = 'saree_products_cache';
const SAREE_TOTAL_KEY = 'saree_total';
const SAREE_FILTERS_KEY = 'saree_filters_cache';

const SareePage = () => {
    const { translateSync } = useTranslation();
    const { getCurrencySymbol, getProductPrice, getProductMrp, formatPriceOnly } = useCurrency();
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [loading, setLoading] = useState(true);
    const [limit, setLimit] = useState(20);
    const [hasMore, setHasMore] = useState(true);
    const [activeFilters, setActiveFilters] = useState({
        priceRange: { min: 0, max: 100000 },
        selectedCategories: [],
        selectedBrands: []
    });
    const [availableSubCategories, setAvailableSubCategories] = useState([]);
    const [availableBrands, setAvailableBrands] = useState([]);

    // Refs to avoid stale closures
    const categoryIdRef = useRef(null);
    const availableBrandsRef = useRef([]);
    const restoredScrollRef = useRef(0);
    const initDoneRef = useRef(false);

    // Tracks the filter set we last actually fetched/restored with.
    // FilterSidebar fires onFilterChange once on mount with the default
    // filters just to "sync up" with the parent — that call happens BEFORE
    // our own init effect (child effects run before parent effects), so it
    // was wiping the sessionStorage cache and forcing a fresh 20-item fetch
    // on every single visit, even right after we'd successfully cached 40+.
    // This ref lets us treat that redundant "same filters as before" call
    // as a no-op instead of nuking a valid cache/restore.
    const DEFAULT_FILTERS_KEY = JSON.stringify({
        priceRange: { min: 0, max: 100000 },
        selectedCategories: [],
        selectedBrands: []
    });
    const prevFiltersKeyRef = useRef(DEFAULT_FILTERS_KEY);

    // ─── Sidebar (brands + subcategories) ──────────────────────────────────────
    const loadSidebarData = async (catId, brandId = "") => {
        try {
            const brandResult = await ApiProvider.getBrandList();
            if (brandResult?.status && brandResult?.response?.data) {
                availableBrandsRef.current = brandResult.response.data;
                setAvailableBrands(brandResult.response.data);
            }
            if (catId) {
                if (brandId) {
                    const brandProdResult = await ApiProvider.getCategoryDet(catId, 1000, 0, { brandId });
                    if (brandProdResult?.status && brandProdResult?.response?.data) {
                        const uniqueSubsMap = new Map();
                        brandProdResult.response.data.forEach(product => {
                            product.subCategoryDetails?.forEach(sub => {
                                if (sub && sub._id) {
                                    uniqueSubsMap.set(sub._id, sub);
                                }
                            });
                        });
                        const subCategoriesForBrand = Array.from(uniqueSubsMap.values());
                        subCategoriesForBrand.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
                        setAvailableSubCategories(subCategoriesForBrand);
                    }
                } else {
                    const subCatResult = await ApiProvider.getSubCategoryList(catId);
                    if (subCatResult?.status && subCatResult?.response?.data) {
                        setAvailableSubCategories(subCatResult.response.data.items || []);
                    }
                }
            }
        } catch (e) { }
    };

    // ─── Resolve category ID ────────────────────────────────────────────────────
    const resolveCategoryId = async () => {
        if (categoryIdRef.current) return categoryIdRef.current;
        const catResult = await ApiProvider.getCategoryList();
        if (catResult?.status && catResult?.response?.data) {
            const sareeCat = catResult.response.data.find(c =>
                c.slug?.toLowerCase() === 'saree' || c.name?.toLowerCase().includes('saree')
            );
            if (sareeCat) {
                categoryIdRef.current = sareeCat._id;
                return sareeCat._id;
            }
        }
        return null;
    };

    // ─── Build a lightweight cache-safe version of a product ───────────────────
    // We only keep the fields the list card + price helpers actually use.
    // The full API objects (rowData, attributes, images, descriptions, etc.)
    // can be large — caching all of it for 40/60/80 products risks hitting
    // sessionStorage's ~5MB quota, which causes a SILENT write failure and
    // leaves a STALE cache behind (see fetchProducts below for why that
    // matters).
    const toLightProduct = (item) => {
        const imgData = item.customerAttribute?.rowData?.[0]?.images?.[0]
            || item.productImage?.[0]
            || item.additionalImage?.[0];

        return {
            _id: item._id,
            productName: item.productName,
            productImage: item.productImage,
            additionalImage: item.additionalImage,
            // Keep fields read directly from root item
            dollarPrice: item.dollarPrice,
            price: item.price,
            offerPrice: item.offerPrice,
            mrpPrice: item.mrpPrice,
            customermrp: item.customermrp,
            customerAttribute: {
                rowData: item.customerAttribute?.rowData?.[0]
                    ? [{
                        images: imgData ? [imgData] : [],
                        // Keep whatever fields getProductPrice / getProductMrp
                        // read from a rowData row. Adjust this list to match
                        // your CurrencyContext implementation if it reads
                        // more/different keys.
                        price: item.customerAttribute.rowData[0].price,
                        mrp: item.customerAttribute.rowData[0].mrp,
                        dollarPrice: item.customerAttribute.rowData[0].dollarPrice,
                        customermrp: item.customerAttribute.rowData[0].customermrp,
                        salePrice: item.customerAttribute.rowData[0].salePrice,
                        discount: item.customerAttribute.rowData[0].discount,
                        stock: item.customerAttribute.rowData[0].stock,
                    }]
                    : []
            }
        };
    };

    // ─── Main fetch ─────────────────────────────────────────────────────────────
    const fetchProducts = async (fetchLimit, filters) => {
        try {
            setLoading(true);

            const catId = await resolveCategoryId();
            if (!catId) return;

            // Load sidebar data if brands not loaded yet
            if (availableBrandsRef.current.length === 0) {
                await loadSidebarData(catId);
            }

            const apiFilters = {
                subCategoryId: filters?.selectedCategories?.[0],
                brandId: filters?.selectedBrands?.[0]
            };
            if (filters?.priceRange && (filters.priceRange.min !== 0 || filters.priceRange.max !== 100000)) {
                apiFilters.priceFromRange = filters.priceRange.min;
                apiFilters.priceToRange = filters.priceRange.max;
            }

            const fetchProductsPromise = ApiProvider.getCategoryDet(catId, fetchLimit, 0, apiFilters);

            // Fetch/Filter Subcategories dynamically based on selected brand
            let subCategoriesPromise;
            const selectedBrandId = filters?.selectedBrands?.[0];
            if (selectedBrandId) {
                subCategoriesPromise = (async () => {
                    const brandProdResult = await ApiProvider.getCategoryDet(catId, 1000, 0, { brandId: selectedBrandId });
                    if (brandProdResult?.status && brandProdResult?.response?.data) {
                        const uniqueSubsMap = new Map();
                        brandProdResult.response.data.forEach(product => {
                            product.subCategoryDetails?.forEach(sub => {
                                if (sub && sub._id) {
                                    uniqueSubsMap.set(sub._id, sub);
                                }
                            });
                        });
                        const subCategoriesForBrand = Array.from(uniqueSubsMap.values());
                        subCategoriesForBrand.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
                        return subCategoriesForBrand;
                    }
                    return [];
                })();
            } else {
                subCategoriesPromise = (async () => {
                    const subCatResult = await ApiProvider.getSubCategoryList(catId);
                    if (subCatResult?.status && subCatResult?.response?.data) {
                        return subCatResult.response.data.items || [];
                    }
                    return [];
                })();
            }

            const [prodResult, subCategories] = await Promise.all([
                fetchProductsPromise,
                subCategoriesPromise
            ]);

            setAvailableSubCategories(subCategories);
            if (prodResult?.status && prodResult?.response?.data) {
                const newProducts = prodResult.response.data;
                const total = prodResult.response.total || 0;

                setProducts(newProducts);
                setTotalResults(total);
                // Use total count (not just "did we get a full page back") so
                // the Show More button hides correctly once everything is loaded.
                setHasMore(newProducts.length < total);

                // ── Cache for back-navigation ──────────────────────────────
                // IMPORTANT: clear the old keys FIRST, then try to write the
                // new cache. If the new write fails (e.g. quota exceeded),
                // the old/stale keys are already gone — so a remount falls
                // through to a fresh fetch instead of silently restoring an
                // outdated, smaller product list (which is what was causing
                // "40 products on screen, but only 20 after coming back").
                try {
                    sessionStorage.removeItem(SAREE_PRODUCTS_KEY);
                    sessionStorage.removeItem(SAREE_TOTAL_KEY);
                    sessionStorage.removeItem(SAREE_LIMIT_KEY);

                    const lightProducts = newProducts.map(toLightProduct);
                    const json = JSON.stringify(lightProducts);

                    sessionStorage.setItem(SAREE_PRODUCTS_KEY, json);
                    sessionStorage.setItem(SAREE_TOTAL_KEY, String(total));
                    sessionStorage.setItem(SAREE_LIMIT_KEY, String(fetchLimit));
                    console.log('[Saree] Cache saved:', fetchLimit, 'products, JSON size:', json.length, 'bytes');
                } catch (e) {
                    console.error('[Saree] sessionStorage write FAILED, cache cleared to avoid stale state:', e);
                    // Keys are already removed above — nothing stale is left behind.
                }
            }
        } catch (err) {
            console.error("Error fetching saree products:", err);
        } finally {
            setLoading(false);
        }
    };

    // ─── Init on mount ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (initDoneRef.current) return;
        initDoneRef.current = true;

        const savedLimit = parseInt(sessionStorage.getItem(SAREE_LIMIT_KEY) || '0', 10);
        const savedScroll = parseInt(sessionStorage.getItem(SAREE_SCROLL_KEY) || '0', 10);
        const savedProductsRaw = sessionStorage.getItem(SAREE_PRODUCTS_KEY);
        const savedTotal = parseInt(sessionStorage.getItem(SAREE_TOTAL_KEY) || '0', 10);
        const savedFiltersRaw = sessionStorage.getItem(SAREE_FILTERS_KEY);

        console.log('[Saree] Init — savedLimit:', savedLimit, 'savedScroll:', savedScroll, 'hasCache:', !!savedProductsRaw);

        if (savedLimit > 0 && savedProductsRaw) {
            try {
                const savedProducts = JSON.parse(savedProductsRaw);
                if (Array.isArray(savedProducts) && savedProducts.length > 0) {
                    console.log('[Saree] Restoring from cache:', savedProducts.length, 'products, scroll to:', savedScroll);
                    
                    let restoredBrandId = "";
                    if (savedFiltersRaw) {
                        try {
                            const parsedFilters = JSON.parse(savedFiltersRaw);
                            setActiveFilters(parsedFilters);
                            prevFiltersKeyRef.current = JSON.stringify(parsedFilters);
                            restoredBrandId = parsedFilters.selectedBrands?.[0] || "";
                        } catch (err) {
                            console.error('[Saree] Failed to parse saved filters', err);
                        }
                    }

                    // Restore from cache instantly
                    setProducts(savedProducts);
                    setTotalResults(savedTotal);
                    setLimit(savedLimit);
                    setHasMore(savedProducts.length < savedTotal);
                    setLoading(false);
                    restoredScrollRef.current = savedScroll;

                    // Load sidebar in background (don't block products) with restored brand selection
                    resolveCategoryId().then(catId => loadSidebarData(catId, restoredBrandId));
                    return;
                }
            } catch (e) {
                console.error('[Saree] Cache parse FAILED:', e);
                // Fall through to fresh fetch
            }
        }

        // Fresh visit
        window.scrollTo(0, 0);
        fetchProducts(20, {
            priceRange: { min: 0, max: 100000 },
            selectedCategories: [],
            selectedBrands: []
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Scroll restore after products paint ────────────────────────────────────
    useEffect(() => {
        if (products.length > 0 && restoredScrollRef.current > 0) {
            const target = restoredScrollRef.current;
            restoredScrollRef.current = 0;
            setTimeout(() => {
                window.scrollTo({ top: target, behavior: 'instant' });
            }, 150);
        }
    }, [products]);

    // ─── Handlers ───────────────────────────────────────────────────────────────
    const handleShowMore = () => {
        const newLimit = limit + 20;
        setLimit(newLimit);
        fetchProducts(newLimit, activeFilters);
    };

    const handleProductClick = (id) => {
        // Save scroll position and active filters before leaving
        const scroll = String(window.scrollY);
        sessionStorage.setItem(SAREE_SCROLL_KEY, scroll);
        sessionStorage.setItem(SAREE_FILTERS_KEY, JSON.stringify(activeFilters));
        console.log('[Saree] Product clicked — scroll saved:', scroll, '| limit in storage:', sessionStorage.getItem(SAREE_LIMIT_KEY));
        navigate(`/saree/${id}`);
    };

    const handleFilterChange = (filters) => {
        const filterKey = JSON.stringify(filters);

        // No-op guard: if this is the same filter set we already fetched/
        // restored with (most commonly FilterSidebar's own "sync on mount"
        // call firing with the default filters), don't touch anything —
        // just keep whatever products are already on screen (fresh fetch
        // or restored-from-cache).
        if (filterKey === prevFiltersKeyRef.current) {
            setActiveFilters(filters);
            return;
        }
        prevFiltersKeyRef.current = filterKey;

        setActiveFilters(filters);
        setLimit(20);
        // Clear cache so fresh fetch happens
        sessionStorage.removeItem(SAREE_LIMIT_KEY);
        sessionStorage.removeItem(SAREE_SCROLL_KEY);
        sessionStorage.removeItem(SAREE_PRODUCTS_KEY);
        sessionStorage.removeItem(SAREE_TOTAL_KEY);
        sessionStorage.removeItem(SAREE_FILTERS_KEY);
        fetchProducts(20, filters);
    };

    // ─── Render ──────────────────────────────────────────────────────────────────
    return (
        <>
            <Helmet>
                <title>Silk Sarees Singapore | Kanchi Korvai Pattu and Pattu Sarees</title>
                <meta name="description" content="Browse Colours Boutique saree collections including Kanchi Korvai Pattu, Korvai Pattu, Kanchipuram silk, wedding and zari border sarees." />
                <link rel="canonical" href="https://coloursboutique.com/saree" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        "name": "Silk Sarees Singapore | Kanchi Korvai Pattu and Pattu Sarees",
                        "url": "https://coloursboutique.com/saree",
                        "description": "Browse Colours Boutique saree collections including Kanchi Korvai Pattu, Korvai Pattu, Kanchipuram silk, wedding and zari border sarees."
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": "Home",
                                "item": "https://coloursboutique.com/"
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "name": "Saree Collection",
                                "item": "https://coloursboutique.com/saree"
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <div className="home-saree-container" style={{ paddingTop: "100px", minHeight: "100vh" }}>
                <section className="saree-section container-fluid px-lg-5">
                    <div className="row g-4 mt-0">
                        {/* Filter Sidebar */}
                        <div className="col-lg-3 sidebar-column">
                            <FilterSidebar
                                onFilterChange={handleFilterChange}
                                availableCategories={availableSubCategories}
                                availableBrands={availableBrands}
                                initialFilters={activeFilters}
                            />
                        </div>

                        {/* Product Area */}
                        <div className="col-lg-9">
                            <div className="saree-section-header" data-aos="fade-down">
                                <h1 className="saree-section-title">Silk Sarees in Singapore</h1>
                                <p className="saree-section-subtitle">A heritage of unique weaving traditions</p>
                            </div>

                            {/* Active Filters / Result count */}
                            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                                <div className="active-tags d-flex align-items-center gap-3">
                                    {activeFilters.priceRange && (
                                        <span style={{ fontSize: '0.85rem', color: '#666', background: '#f5f5f5', padding: '4px 12px', borderRadius: '15px' }}>
                                            {getCurrencySymbol()} {formatPriceOnly(activeFilters.priceRange.min).toLocaleString()} — {getCurrencySymbol()} {formatPriceOnly(activeFilters.priceRange.max).toLocaleString()}
                                        </span>
                                    )}
                                    <span className="text-muted small">Showing {totalResults} results</span>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center mt-5">Loading Collections...</div>
                            ) : products.length > 0 ? (
                                <div className="collection-grid mt-2">
                                    {products.map((item, idx) => {
                                        const imgData = item.customerAttribute?.rowData?.[0]?.images?.[0]
                                            || item.productImage?.[0]
                                            || item.additionalImage?.[0];
                                        const imgSrc = imgData
                                            ? `${IMAGE_URL}${imgData.docPath}/${imgData.docName}`
                                            : '/img/products/default.png';

                                        const firstRow = item.customerAttribute?.rowData?.[0];
                                        const priceValue = getProductPrice(item, firstRow);
                                        const displayPrice = priceValue > 0 ? priceValue : null;

                                        return (
                                            <div
                                                key={item._id || idx}
                                                className="collection-item"
                                                onClick={() => handleProductClick(item._id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="collection-image-wrapper">
                                                    <img
                                                        src={imgSrc}
                                                        alt={item.productName}
                                                        className="collection-image"
                                                        onError={(e) => { e.target.src = '/img/products/default.png'; }}
                                                    />
                                                </div>
                                                <div className="text-start mt-3">
                                                    <h4 style={{
                                                        fontSize: '1.15rem',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        marginBottom: '8px',
                                                        lineHeight: '1.4',
                                                        textTransform: 'capitalize'
                                                    }}>
                                                        {item.productName}
                                                    </h4>
                                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                                        <div className="d-flex flex-wrap align-items-baseline gap-2">
                                                            {displayPrice === null ? (
                                                                <span className="text-muted" style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                                                                    Currency not available
                                                                </span>
                                                            ) : (
                                                                <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#B12704' }}>
                                                                    {getCurrencySymbol()}{displayPrice.toLocaleString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-5 w-100">
                                    <p className="text-muted" style={{ fontSize: '1.2rem', letterSpacing: '1px' }}>
                                        {translateSync('No products found in this collection.')}
                                    </p>
                                </div>
                            )}

                            {!loading && hasMore && products.length > 0 && (
                                <div className="text-center mt-5">
                                    <button
                                        className="btn btn-dark px-5 py-2 text-uppercase rounded-0"
                                        onClick={handleShowMore}
                                        style={{ letterSpacing: '2px', fontWeight: 'bold' }}
                                    >
                                        {translateSync('Show More')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default SareePage;